// ============================================
// INVESTORQ SUBSCRIPTION SETTINGS COMPONENT
// Updated with Options Pro support + Date fix
// ============================================

import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Check,
  X,
  Crown,
  Zap,
  Star,
  Loader,
  AlertTriangle,
  ChevronRight,
  Shield,
  Clock,
  TrendingUp,
  BarChart3,
  Target,
  Briefcase,
  Building,
  Activity,
  RefreshCw,
  ExternalLink,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// 🆕 Helper function to safely parse dates
const formatDate = (dateValue) => {
  if (!dateValue) return null;
  
  // Handle Unix timestamp (seconds)
  if (typeof dateValue === 'number') {
    // If it's a small number, it's likely seconds - convert to milliseconds
    const timestamp = dateValue < 10000000000 ? dateValue * 1000 : dateValue;
    const date = new Date(timestamp);
    // Check for invalid date (1970 = epoch issues)
    if (date.getFullYear() < 2020) return null;
    return date.toLocaleDateString();
  }
  
  // Handle string date
  if (typeof dateValue === 'string') {
    const date = new Date(dateValue);
    if (isNaN(date.getTime()) || date.getFullYear() < 2020) return null;
    return date.toLocaleDateString();
  }
  
  return null;
};

const SubscriptionSettings = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [optionsProSubscription, setOptionsProSubscription] = useState(null); // 🆕
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [optionsBillingCycle, setOptionsBillingCycle] = useState('monthly'); // 🆕
  const [upgrading, setUpgrading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // 🆕
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchSubscriptionData();
  }, [user]);

  const fetchSubscriptionData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 🆕 Fetch from /stripe/plans and /stripe/subscription-status
      const [statusRes, plansRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/stripe/subscription-status/${user.id}`),
        fetch(`${API_URL}/stripe/plans`),
        fetch(`${API_URL}/subscription/usage`, {
          headers: { 'x-user-id': user.id }
        }).catch(() => ({ json: () => ({ today: {} }) })) // Graceful fallback
      ]);

      const statusData = await statusRes.json();
      const plansData = await plansRes.json();
      const usageData = await usageRes.json();

      if (statusData.success) {
        setSubscription(statusData.subscription);
        setOptionsProSubscription(statusData.optionsPro); // 🆕
      }
      setPlans(plansData.plans || []);
      setUsage(usageData);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Handle Stripe checkout for both main and Options Pro
  const handleSubscribe = async (plan, isOptionsPro = false) => {
    if (!user) {
      setError('Please log in to subscribe');
      return;
    }

    const cycle = isOptionsPro ? optionsBillingCycle : billingCycle;
    const priceId = cycle === 'monthly' 
      ? plan.priceIds?.monthly 
      : plan.priceIds?.yearly;

    if (!priceId) {
      setError('Invalid plan selected');
      return;
    }

    setCheckoutLoading(plan.id);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          userId: user.id,
          userEmail: user.email,
          productType: isOptionsPro ? 'options_pro' : 'main'
        })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError('Failed to start checkout process');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      setError('Failed to open billing portal');
    }
  };

  const handleCancel = async (subscriptionType = 'main') => {
    setCancelling(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/stripe/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: user.id,
          subscriptionType 
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Subscription cancelled. You will retain access until the end of your billing period.');
        setShowCancelConfirm(false);
        await fetchSubscriptionData();
      } else {
        setError(data.error || 'Cancellation failed');
      }
    } catch (err) {
      setError('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async (subscriptionType = 'main') => {
    setUpgrading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/stripe/reactivate-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          userId: user.id,
          subscriptionType 
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Subscription reactivated!');
        await fetchSubscriptionData();
      } else {
        setError(data.error || 'Reactivation failed');
      }
    } catch (err) {
      setError('Failed to reactivate subscription');
    } finally {
      setUpgrading(false);
    }
  };

  // Plan feature icons
  const getFeatureIcon = (feature) => {
    if (feature.includes('stock')) return <TrendingUp size={14} />;
    if (feature.includes('Options') || feature.includes('options')) return <Target size={14} />;
    if (feature.includes('Portfolio') || feature.includes('portfolio')) return <Briefcase size={14} />;
    if (feature.includes('Congress') || feature.includes('Whale') || feature.includes('Insider')) return <Building size={14} />;
    if (feature.includes('AI')) return <Zap size={14} />;
    if (feature.includes('Analytics')) return <BarChart3 size={14} />;
    return <Check size={14} />;
  };

  if (!user) {
    return (
      <div style={{
        background: 'rgba(37, 99, 235, 0.1)',
        border: '1px solid rgba(37, 99, 235, 0.3)',
        borderRadius: '0.75rem',
        padding: '3rem',
        textAlign: 'center'
      }}>
        <CreditCard size={48} color="#60a5fa" style={{ margin: '0 auto 1rem' }} />
        <h3 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Login Required</h3>
        <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
          Please log in to manage your subscription
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
        <Loader size={48} color="#60a5fa" className="spin" />
      </div>
    );
  }

  const currentTier = subscription?.tier || 'free';
  const isTrialing = subscription?.isTrialing;
  const isCancelled = subscription?.cancelAtPeriodEnd;

  // Filter main plans vs options pro
  const mainPlans = plans.filter(p => !p.isAddon);
  const optionsProPlan = plans.find(p => p.id === 'options_pro');

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
        borderRadius: '0.75rem',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Crown size={32} color="white" />
          Subscription
        </h2>
        <p style={{ color: '#e0e7ff', fontSize: '1rem', margin: 0 }}>
          Manage your InvestorQ subscription and billing
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <AlertTriangle size={20} color="#f87171" />
          <span style={{ color: '#fca5a5' }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#f87171', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {success && (
        <div style={{
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <Check size={20} color="#22c55e" />
          <span style={{ color: '#86efac' }}>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#22c55e', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Current Plan Status */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.5)',
        border: '1px solid #334155',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
              Current Plan
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '0.5rem',
              flexWrap: 'wrap'
            }}>
              <span style={{
                fontSize: '1.75rem',
                fontWeight: 'bold',
                color: currentTier === 'premium' ? '#fbbf24' : currentTier === 'pro' ? '#a78bfa' : '#60a5fa'
              }}>
                {currentTier === 'premium' ? '👑 Premium' : currentTier === 'pro' ? '💎 Pro' : '🆓 Free'}
              </span>
              {isTrialing && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(245, 158, 11, 0.2)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '1rem',
                  fontSize: '0.75rem',
                  color: '#fbbf24',
                  fontWeight: '600'
                }}>
                  Trial
                </span>
              )}
              {isCancelled && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '1rem',
                  fontSize: '0.75rem',
                  color: '#f87171',
                  fontWeight: '600'
                }}>
                  Cancelling
                </span>
              )}
              {/* 🆕 Show Options Pro badge */}
              {optionsProSubscription?.active && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  background: 'rgba(249, 115, 22, 0.2)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '1rem',
                  fontSize: '0.75rem',
                  color: '#f97316',
                  fontWeight: '600'
                }}>
                  + Options Pro
                </span>
              )}
            </div>
            {subscription?.currentPeriodEnd && formatDate(subscription.currentPeriodEnd) && (
              <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
                <Clock size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                {isCancelled ? 'Access until: ' : isTrialing ? 'Trial ends: ' : 'Renews: '}
                {formatDate(subscription.currentPeriodEnd)}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {isCancelled && (
              <button
                onClick={() => handleReactivate('main')}
                disabled={upgrading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#22c55e',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: upgrading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '600'
                }}
              >
                {upgrading ? <Loader size={16} className="spin" /> : <RefreshCw size={16} />}
                Reactivate
              </button>
            )}
            <button
              onClick={handleManageSubscription}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                border: '1px solid #6366f1',
                borderRadius: '0.5rem',
                color: '#a78bfa',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ExternalLink size={16} />
              Manage Billing
            </button>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid #ef4444'
          }}>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#f87171' }}>
              Cancel Subscription?
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>
              Your subscription will remain active until the end of your current billing period.
              After that, you'll be downgraded to the Free plan.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => setShowCancelConfirm(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#334155',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Keep Subscription
              </button>
              <button
                onClick={() => handleCancel('main')}
                disabled={cancelling}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: cancelling ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {cancelling ? <Loader size={16} className="spin" /> : null}
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '0.5rem',
          padding: '0.25rem',
          border: '1px solid #334155'
        }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '0.75rem 1.5rem',
              background: billingCycle === 'monthly' ? '#6366f1' : 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              color: billingCycle === 'monthly' ? 'white' : '#94a3b8',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            style={{
              padding: '0.75rem 1.5rem',
              background: billingCycle === 'yearly' ? '#6366f1' : 'transparent',
              border: 'none',
              borderRadius: '0.375rem',
              color: billingCycle === 'yearly' ? 'white' : '#94a3b8',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Yearly
            <span style={{
              padding: '0.125rem 0.5rem',
              background: '#22c55e',
              borderRadius: '1rem',
              fontSize: '0.65rem',
              color: 'white'
            }}>
              Save 30%
            </span>
          </button>
        </div>
      </div>

      {/* Main Pricing Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {mainPlans.map((plan) => {
          const isCurrentPlan = currentTier === plan.id;
          const isUpgrade = plan.id !== 'free' && (
            (currentTier === 'free') ||
            (currentTier === 'pro' && plan.id === 'premium')
          );
          const isDowngrade = (currentTier === 'premium' && plan.id !== 'premium') ||
                             (currentTier === 'pro' && plan.id === 'free');
          
          const monthlyPrice = billingCycle === 'yearly' 
            ? (plan.price?.yearly / 12).toFixed(2) 
            : plan.price?.monthly || 0;
          
          const planColors = {
            free: { bg: 'rgba(59, 130, 246, 0.1)', border: '#3b82f6', accent: '#60a5fa' },
            pro: { bg: 'rgba(139, 92, 246, 0.1)', border: '#8b5cf6', accent: '#a78bfa' },
            premium: { bg: 'rgba(251, 191, 36, 0.1)', border: '#fbbf24', accent: '#fcd34d' }
          };
          
          const colors = planColors[plan.id] || planColors.free;

          return (
            <div
              key={plan.id}
              style={{
                background: isCurrentPlan ? colors.bg : 'rgba(30, 41, 59, 0.5)',
                border: `2px solid ${isCurrentPlan ? colors.border : '#334155'}`,
                borderRadius: '1rem',
                padding: '1.5rem',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '0.25rem 1rem',
                  background: '#10b981',
                  borderRadius: '1rem',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  color: 'white'
                }}>
                  MOST POPULAR
                </div>
              )}

              {/* Plan Header */}
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: colors.accent,
                  marginBottom: '0.5rem'
                }}>
                  {plan.id === 'premium' ? '👑' : plan.id === 'pro' ? '💎' : '🆓'} {plan.name}
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: 0 }}>
                  {plan.description}
                </p>
              </div>

              {/* Price */}
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>
                    ${monthlyPrice}
                  </span>
                  <span style={{ color: '#94a3b8' }}>/mo</span>
                </div>
                {billingCycle === 'yearly' && plan.price?.yearly > 0 && (
                  <div style={{ fontSize: '0.875rem', color: '#22c55e' }}>
                    Billed ${plan.price.yearly}/year
                  </div>
                )}
              </div>

              {/* Features */}
              <div style={{ marginBottom: '1.5rem' }}>
                {(plan.features || []).map((feature, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.5rem 0',
                      borderBottom: idx < plan.features.length - 1 ? '1px solid rgba(51, 65, 85, 0.5)' : 'none'
                    }}
                  >
                    <div style={{ color: colors.accent }}>
                      {getFeatureIcon(feature)}
                    </div>
                    <span style={{ fontSize: '0.875rem', color: '#cbd5e1' }}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
              <button
                onClick={() => isUpgrade && handleSubscribe(plan)}
                disabled={isCurrentPlan || isDowngrade || checkoutLoading === plan.id || plan.id === 'free'}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: isCurrentPlan
                    ? 'transparent'
                    : plan.id === 'free'
                      ? '#334155'
                      : isUpgrade
                        ? `linear-gradient(135deg, ${colors.border} 0%, ${colors.accent} 100%)`
                        : '#334155',
                  border: isCurrentPlan ? `2px solid ${colors.border}` : 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  cursor: isCurrentPlan || isDowngrade || plan.id === 'free' ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  opacity: isDowngrade ? 0.5 : 1
                }}
              >
                {checkoutLoading === plan.id ? (
                  <Loader size={18} className="spin" />
                ) : isCurrentPlan ? (
                  <>
                    <Check size={18} />
                    Current Plan
                  </>
                ) : plan.id === 'free' ? (
                  'Free Forever'
                ) : isUpgrade ? (
                  <>
                    <Zap size={18} />
                    Upgrade to {plan.name}
                  </>
                ) : (
                  'Downgrade'
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* 🆕 OPTIONS PRO ADD-ON SECTION */}
      {optionsProPlan && (
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              <Target size={24} color="#f59e0b" />
              Options Trading Add-On
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
              Professional options trading tools - Subscribe separately
            </p>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
            border: '2px solid rgba(245, 158, 11, 0.5)',
            borderRadius: '1rem',
            padding: '1.5rem',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            {/* Options Pro Status */}
            {optionsProSubscription?.active && (
              <div style={{
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: 'wrap'
              }}>
                <Check size={18} color="#10b981" />
                <span style={{ color: '#10b981', fontWeight: '600' }}>
                  Options Pro Active
                </span>
                {optionsProSubscription.isTrialing && (
                  <span style={{
                    padding: '0.125rem 0.5rem',
                    background: '#f59e0b',
                    borderRadius: '1rem',
                    fontSize: '0.7rem',
                    color: 'white',
                    marginLeft: '0.5rem'
                  }}>
                    Trial
                  </span>
                )}
                {formatDate(optionsProSubscription.currentPeriodEnd) && (
                  <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: '0.875rem' }}>
                    {optionsProSubscription.isTrialing ? 'Trial ends ' : 'Renews '}
                    {formatDate(optionsProSubscription.currentPeriodEnd)}
                  </span>
                )}
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 600 ? '1fr' : '1fr 1fr',
              gap: '1.5rem',
              alignItems: 'start'
            }}>
              {/* Left: Features */}
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: '#f59e0b' }}>
                  🎯 Included Features
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {(optionsProPlan.features || []).map((feature, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.6rem',
                      color: '#e2e8f0',
                      fontSize: '0.9rem'
                    }}>
                      <Check size={14} color="#f59e0b" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: Pricing & CTA */}
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                padding: '1.25rem'
              }}>
                {/* Billing Toggle */}
                <div style={{
                  display: 'flex',
                  background: 'rgba(15, 23, 42, 0.5)',
                  borderRadius: '0.5rem',
                  padding: '0.25rem',
                  marginBottom: '1rem'
                }}>
                  <button
                    onClick={() => setOptionsBillingCycle('monthly')}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '0.375rem',
                      border: 'none',
                      background: optionsBillingCycle === 'monthly' ? '#f59e0b' : 'transparent',
                      color: optionsBillingCycle === 'monthly' ? 'white' : '#94a3b8',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8rem'
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setOptionsBillingCycle('yearly')}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '0.375rem',
                      border: 'none',
                      background: optionsBillingCycle === 'yearly' ? '#f59e0b' : 'transparent',
                      color: optionsBillingCycle === 'yearly' ? 'white' : '#94a3b8',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.8rem'
                    }}
                  >
                    Yearly
                  </button>
                </div>

                {/* Price */}
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
                    <span style={{ fontSize: '2.5rem', fontWeight: '700', color: '#f59e0b' }}>
                      ${optionsBillingCycle === 'monthly' 
                        ? optionsProPlan.price?.monthly 
                        : Math.round(optionsProPlan.price?.yearly / 12)
                      }
                    </span>
                    <span style={{ color: '#94a3b8', fontSize: '1rem' }}>/mo</span>
                  </div>
                  {optionsBillingCycle === 'yearly' && (
                    <>
                      <p style={{ color: '#10b981', fontSize: '0.8rem', margin: '0.25rem 0' }}>
                        ${optionsProPlan.price?.yearly} billed annually
                      </p>
                      <p style={{ color: '#f59e0b', fontSize: '0.75rem', margin: 0 }}>
                        Save ${(optionsProPlan.price?.monthly * 12 - optionsProPlan.price?.yearly)}/year
                      </p>
                    </>
                  )}
                </div>

                {/* CTA */}
                <button
                  onClick={() => handleSubscribe(optionsProPlan, true)}
                  disabled={optionsProSubscription?.active || checkoutLoading === 'options_pro'}
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: optionsProSubscription?.active
                      ? '#475569'
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    fontSize: '0.95rem',
                    fontWeight: '700',
                    cursor: optionsProSubscription?.active ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {checkoutLoading === 'options_pro' ? (
                    <>
                      <Loader size={18} className="spin" />
                      Processing...
                    </>
                  ) : optionsProSubscription?.active ? (
                    <>
                      <Check size={18} />
                      Currently Subscribed
                    </>
                  ) : (
                    <>
                      <Target size={18} />
                      Start 7-Day Free Trial
                    </>
                  )}
                </button>

                {optionsProSubscription?.active && (
                  <button
                    onClick={handleManageSubscription}
                    style={{
                      width: '100%',
                      marginTop: '0.5rem',
                      padding: '0.6rem',
                      background: 'transparent',
                      border: '1px solid #f59e0b',
                      borderRadius: '0.5rem',
                      color: '#f59e0b',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.85rem'
                    }}
                  >
                    Manage Options Subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Statistics */}
      {usage && Object.keys(usage.today || {}).length > 0 && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.5)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Activity size={20} color="#60a5fa" />
            Today's Usage
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {Object.entries(usage.today || {}).map(([feature, count]) => (
              <div
                key={feature}
                style={{
                  background: 'rgba(51, 65, 85, 0.5)',
                  borderRadius: '0.5rem',
                  padding: '1rem'
                }}
              >
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                  {feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#60a5fa' }}>
                  {count}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '1rem',
            fontSize: '0.75rem',
            color: '#64748b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Clock size={14} />
            Usage resets at midnight UTC
          </div>
        </div>
      )}

      {/* FAQ / Help */}
      <div style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#93c5fd' }}>
          💡 Subscription FAQ
        </h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div>
            <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
              Can I have both subscriptions?
            </div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
              Yes! Options Pro is a separate add-on that can be combined with any plan.
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
              Can I cancel anytime?
            </div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
              Yes! Cancel anytime and keep access until the end of your billing period.
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#cbd5e1', marginBottom: '0.25rem' }}>
              Do you offer refunds?
            </div>
            <div style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
              We offer a 7-day free trial. Cancel before trial ends to avoid charges.
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SubscriptionSettings;